import { inject, Injectable } from '@angular/core';
import { LOCALSTORAGE_TOKENS } from '../constants/injection-tokens';
import { LocalstorageTokensKeys } from '../../shared/models/tokens-keys.interface';
import { UserData } from '../../features/auth/interfaces/auth.interface';

@Injectable({ providedIn: 'root' })
export class LocalStorageService {
  private readonly localstorageTokensKeys =
    inject<LocalstorageTokensKeys>(LOCALSTORAGE_TOKENS);

  setItem(key: string, value: unknown): void {
    localStorage.setItem(key, JSON.stringify(value));
  }

  getItem<T>(key: string): T | null {
    const item = localStorage.getItem(key);
    return item && item != 'undefined' ? (JSON.parse(item) as T) : null;
  }

  removeItem(key: string): void {
    localStorage.removeItem(key);
  }

  clear(): void {
    Object.values(this.localstorageTokensKeys).forEach((key) => {
      if (key === this.localstorageTokensKeys.lang) {
        return;
      }
      this.removeItem(key);
    });
  }

  // User data management methods
  setUserData(userData: UserData): void {
    this.setItem(this.localstorageTokensKeys.userData, userData);
  }

  getUserData(): UserData | null {
    return this.getItem<UserData>(this.localstorageTokensKeys.userData);
  }

  clearUserData(): void {
    this.removeItem(this.localstorageTokensKeys.userData);
  }

  // Token management methods
  setAccessToken(token: string): void {
    this.setItem(this.localstorageTokensKeys.accessToken, token);
  }

  getAccessToken(): string | null {
    return this.getItem<string>(this.localstorageTokensKeys.accessToken);
  }

  setRefreshToken(token: string): void {
    this.setItem(this.localstorageTokensKeys.refreshToken, token);
  }

  getRefreshToken(): string | null {
    return this.getItem<string>(this.localstorageTokensKeys.refreshToken);
  }

  setTokenExpiry(expiry: number): void {
    this.setItem(this.localstorageTokensKeys.tokenExpiry, expiry);
  }

  getTokenExpiry(): number | null {
    return this.getItem<number>(this.localstorageTokensKeys.tokenExpiry);
  }

  // Subscription status, known from OTP verification and completed checkouts
  setSubscribed(isSubscribed: boolean): void {
    this.setItem(this.localstorageTokensKeys.isSubscribed, isSubscribed);
  }

  isSubscribed(): boolean {
    return this.getItem<boolean>(this.localstorageTokensKeys.isSubscribed) === true;
  }

  clearTokenExpiry(): void {
    this.removeItem(this.localstorageTokensKeys.tokenExpiry);
  }

  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    if (!token) {
      return false;
    }
    // Tokens without a known expiry are trusted until the API rejects them
    // (the token interceptor then refreshes or redirects).
    const expiry = this.getTokenExpiry();
    return !expiry || Date.now() < expiry;
  }
}
